import { useEffect, useState, type ReactNode } from 'react';
import Sidebar from '../components/dashboard/Sidebar';
import Topbar from '../components/dashboard/Topbar';
import { C2Button } from '../components/c2';
import { api, clearToken, getToken, type User } from '../lib/api';

interface SectionProps {
  title: string;
  description?: string;
  children: ReactNode;
  danger?: boolean;
}

function Section({ title, description, children, danger }: SectionProps) {
  return (
    <div
      className="bg-surface mb-6"
      style={{
        border: `1px solid ${danger ? 'var(--color-peach)' : 'var(--color-hairline)'}`,
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '18px 22px 14px',
          borderBottom: '1px solid var(--color-hairline)',
          background: danger ? 'oklch(0.98 0.012 40)' : 'transparent',
        }}
      >
        <div
          className="font-display font-bold text-ink m-0"
          style={{ fontSize: 17, letterSpacing: '-0.01em', color: danger ? 'var(--color-peach-deep)' : 'var(--color-ink)' }}
        >
          {title}
        </div>
        {description && (
          <div className="text-[13px] text-ink-soft mt-1">
            {description}
          </div>
        )}
      </div>
      <div style={{ padding: '20px 22px' }}>{children}</div>
    </div>
  );
}

interface FieldRowProps {
  label: string;
  children: ReactNode;
  hint?: ReactNode;
}

function FieldRow({ label, children, hint }: FieldRowProps) {
  return (
    <div className="mb-4 last:mb-0">
      <label className="block text-[12.5px] font-semibold text-ink mb-1.5">{label}</label>
      {children}
      {hint && <div className="text-[12px] text-ink-muted mt-1.5">{hint}</div>}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  border: '1px solid var(--color-hairline-strong)',
  borderRadius: 14,
  fontSize: 14,
  color: 'var(--color-ink)',
  background: 'var(--color-surface)',
  fontFamily: 'inherit',
  outline: 'none',
};

interface ProfileSectionProps {
  user: User;
  onSaved: () => void;
}

function ProfileSection({ user, onSaved }: ProfileSectionProps) {
  const [name, setName] = useState(user.name ?? '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const dirty = name.trim() !== (user.name ?? '').trim();

  const onSave = async () => {
    if (!name.trim()) {
      setMsg({ kind: 'err', text: 'Name cannot be empty' });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      await api.updateProfile({ name: name.trim() });
      setMsg({ kind: 'ok', text: 'Profile updated' });
      onSaved();
    } catch (err) {
      setMsg({ kind: 'err', text: (err as Error).message || 'Could not update profile' });
    } finally {
      setSaving(false);
    }
  };

  // Magic-link-by-email is always available regardless of what else is on file —
  // surface it explicitly so users on a magic-link-only account don't see "None".
  const providers: string[] = ['Email magic link'];
  if (user.has_password) providers.push('Password');
  if (user.has_google) providers.push('Google');
  if (user.has_microsoft) providers.push('Microsoft');
  if (user.has_zoho) providers.push('Zoho');

  return (
    <Section title="Profile" description="How you appear to teammates.">
      <FieldRow label="Display name">
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          style={inputStyle}
          placeholder="Your name"
        />
      </FieldRow>

      <FieldRow label="Email" hint="Contact support if you need to change your email address.">
        <input value={user.email} disabled style={{ ...inputStyle, background: 'var(--color-bg-alt)', color: 'var(--color-ink-soft)' }} />
      </FieldRow>

      <FieldRow label="Sign-in methods">
        <div className="flex flex-wrap gap-1.5">
          {providers.length === 0 ? (
            <span className="text-[13px] text-ink-muted">None on file</span>
          ) : (
            providers.map(p => (
              <span
                key={p}
                className="inline-block text-xs font-semibold"
                style={{
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: 'var(--color-bg-alt)',
                  color: 'var(--color-ink-soft)',
                  border: '1px solid var(--color-hairline)',
                }}
              >
                {p}
              </span>
            ))
          )}
        </div>
      </FieldRow>

      <div className="flex items-center gap-3 mt-2">
        <C2Button onClick={onSave} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </C2Button>
        {msg && (
          <span className="text-[13px]" style={{ color: msg.kind === 'ok' ? 'var(--color-mint-deep)' : 'var(--color-peach-deep)' }}>
            {msg.text}
          </span>
        )}
      </div>
    </Section>
  );
}

interface PasswordSectionProps {
  user: User;
}

function PasswordSection({ user }: PasswordSectionProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  if (!user.has_password) {
    const reason = (user.has_google || user.has_microsoft || user.has_zoho)
      ? "You sign in with a third-party provider, so there's no password on this account."
      : "You sign in with a magic link sent to your email — no password is set.";
    return (
      <Section title="Password" description={reason}>
        <div className="text-[13px] text-ink-soft">
          To set a password, sign out and use the "Forgot password" flow on the sign-in page.
        </div>
      </Section>
    );
  }

  const onSubmit = async () => {
    setMsg(null);
    if (!currentPassword || !newPassword) {
      setMsg({ kind: 'err', text: 'Fill in both fields' });
      return;
    }
    if (newPassword.length < 6) {
      setMsg({ kind: 'err', text: 'New password must be at least 6 characters' });
      return;
    }
    if (newPassword !== confirm) {
      setMsg({ kind: 'err', text: 'New password and confirmation do not match' });
      return;
    }
    setSaving(true);
    try {
      await api.changePassword({ currentPassword, newPassword });
      setMsg({ kind: 'ok', text: 'Password updated' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
    } catch (err) {
      setMsg({ kind: 'err', text: (err as Error).message || 'Could not update password' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Password" description="Use a strong password you don't reuse elsewhere.">
      <FieldRow label="Current password">
        <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} style={inputStyle} autoComplete="current-password" />
      </FieldRow>
      <FieldRow label="New password" hint="At least 6 characters.">
        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={inputStyle} autoComplete="new-password" />
      </FieldRow>
      <FieldRow label="Confirm new password">
        <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} style={inputStyle} autoComplete="new-password" />
      </FieldRow>
      <div className="flex items-center gap-3 mt-2">
        <C2Button onClick={onSubmit} disabled={saving}>
          {saving ? 'Updating…' : 'Update password'}
        </C2Button>
        {msg && (
          <span className="text-[13px]" style={{ color: msg.kind === 'ok' ? 'var(--color-mint-deep)' : 'var(--color-peach-deep)' }}>
            {msg.text}
          </span>
        )}
      </div>
    </Section>
  );
}

interface WorkspaceSectionProps {
  user: User;
  onSaved: () => void;
}

function WorkspaceSection({ user, onSaved }: WorkspaceSectionProps) {
  const initial = user.workspace?.ws_name?.trim() ?? '';
  const [name, setName] = useState(initial);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const dirty = name.trim() !== initial && name.trim().length > 0;

  const onSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await api.updateWorkspace({ name: name.trim() });
      setMsg({ kind: 'ok', text: 'Workspace renamed' });
      setConfirmOpen(false);
      onSaved();
    } catch (err) {
      setMsg({ kind: 'err', text: (err as Error).message || 'Could not rename workspace' });
    } finally {
      setSaving(false);
    }
  };

  if (user.workspace?.is_personal) {
    return null;
  }

  return (
    <Section title="Workspace" description="Anyone on your team can rename the workspace.">
      <FieldRow
        label="Workspace name"
        hint={`Domain: ${user.workspace?.domain ?? '—'}`}
      >
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          style={inputStyle}
          placeholder="Acme Inc."
        />
      </FieldRow>
      <div className="flex items-center gap-3 mt-2">
        <C2Button onClick={() => setConfirmOpen(true)} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Rename workspace'}
        </C2Button>
        {msg && (
          <span className="text-[13px]" style={{ color: msg.kind === 'ok' ? 'var(--color-mint-deep)' : 'var(--color-peach-deep)' }}>
            {msg.text}
          </span>
        )}
      </div>

      {confirmOpen && (
        <ConfirmModal
          title="Rename workspace?"
          body={
            <>
              <p className="text-[14px] text-ink-soft mb-3 m-0 leading-[1.5]">
                You're renaming the workspace to <strong className="text-ink">"{name.trim()}"</strong>. This change is visible to <strong className="text-ink">every teammate</strong> the next time they load the dashboard.
              </p>
              <p className="text-[13px] text-ink-muted m-0">
                Anyone on your team can change it again later.
              </p>
            </>
          }
          confirmLabel={saving ? 'Renaming…' : 'Yes, rename for everyone'}
          confirmDisabled={saving}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={onSave}
        />
      )}
    </Section>
  );
}

function ExportSection() {
  const [busy, setBusy] = useState<'csv' | 'json' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const download = async (format: 'csv' | 'json') => {
    setBusy(format);
    setErr(null);
    try {
      const token = getToken();
      const res = await fetch(`/links/export?format=${format}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match?.[1] ?? `tidy-go-links.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr((e as Error).message || 'Could not export');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Section title="Export" description="Download every go/ link in your workspace. Your data, always.">
      <div className="flex items-center gap-3 flex-wrap">
        <C2Button onClick={() => download('csv')} disabled={busy !== null}>
          {busy === 'csv' ? 'Preparing…' : 'Download CSV'}
        </C2Button>
        <C2Button variant="ghost" onClick={() => download('json')} disabled={busy !== null}>
          {busy === 'json' ? 'Preparing…' : 'Download JSON'}
        </C2Button>
        {err && (
          <span className="text-[13px]" style={{ color: 'var(--color-peach-deep)' }}>
            {err}
          </span>
        )}
      </div>
    </Section>
  );
}

interface DangerSectionProps {
  user: User;
}

function DangerSection({ user }: DangerSectionProps) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isPersonal = !!user.workspace?.is_personal;
  const others = user.workspace_other_members ?? 0;
  const linkCount = user.link_count ?? 0;
  const cascade = isPersonal || others === 0;

  let warning: ReactNode;
  if (isPersonal) {
    warning = (
      <>
        <p className="text-[14px] text-ink m-0 mb-2 leading-[1.5]">
          Your account and all <strong>{linkCount} go/ link{linkCount === 1 ? '' : 's'}</strong> will be permanently deleted.
        </p>
        <p className="text-[13px] text-ink-soft m-0">This cannot be undone.</p>
      </>
    );
  } else if (others === 0) {
    warning = (
      <>
        <p className="text-[14px] text-ink m-0 mb-2 leading-[1.5]">
          You're the last member of this workspace. Deleting your account will permanently delete the workspace and all <strong>{linkCount} go/ link{linkCount === 1 ? '' : 's'}</strong>.
        </p>
        <p className="text-[13px] text-ink-soft m-0">This cannot be undone.</p>
      </>
    );
  } else {
    warning = (
      <>
        <p className="text-[14px] text-ink m-0 mb-2 leading-[1.5]">
          Your account will be deleted. Your <strong>{linkCount} go/ link{linkCount === 1 ? '' : 's'}</strong> will remain in the workspace and show "Deleted user" as the creator.
        </p>
        <p className="text-[13px] text-ink-soft m-0">
          Teammates can claim them to take over editing.
        </p>
      </>
    );
  }

  const onDelete = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.deleteAccount();
      clearToken();
      window.location.href = '/';
    } catch (e) {
      setErr((e as Error).message || 'Could not delete account');
      setBusy(false);
    }
  };

  return (
    <Section
      title="Danger zone"
      description="Permanent and irreversible actions."
      danger
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="text-[14px] font-semibold text-ink mb-1">Delete your account</div>
          <div className="text-[13px] text-ink-soft">
            {cascade
              ? 'Removes your account and wipes the workspace.'
              : 'Removes your account. Your links stay in the workspace.'}
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="cursor-pointer font-semibold whitespace-nowrap"
          style={{
            padding: '10px 18px',
            borderRadius: 999,
            background: 'transparent',
            color: 'var(--color-peach-deep)',
            border: '1.5px solid var(--color-peach-deep)',
            fontSize: 14,
          }}
        >
          Delete account
        </button>
      </div>

      {open && (
        <ConfirmModal
          title="Delete your account?"
          danger
          body={
            <>
              {warning}
              {err && <p className="text-[13px] mt-3 m-0" style={{ color: 'var(--color-peach-deep)' }}>{err}</p>}
              <div className="mt-4">
                <label className="block text-[12.5px] font-semibold text-ink mb-1.5">
                  Type <span className="font-mono" style={{ background: 'var(--color-bg-alt)', padding: '1px 6px', borderRadius: 4 }}>DELETE</span> to confirm
                </label>
                <input
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  style={inputStyle}
                  autoFocus
                  placeholder="DELETE"
                />
              </div>
            </>
          }
          confirmLabel={busy ? 'Deleting…' : 'Delete account permanently'}
          confirmDisabled={busy || confirmText !== 'DELETE'}
          confirmDanger
          onCancel={() => { setOpen(false); setConfirmText(''); setErr(null); }}
          onConfirm={onDelete}
        />
      )}
    </Section>
  );
}

interface ConfirmModalProps {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  confirmDisabled?: boolean;
  confirmDanger?: boolean;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function ConfirmModal({ title, body, confirmLabel, confirmDisabled, confirmDanger, onCancel, onConfirm }: ConfirmModalProps) {
  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[100]"
      style={{ background: 'oklch(0.2 0.02 260 / 0.4)', padding: 20 }}
      onClick={onCancel}
    >
      <div
        className="w-full bg-surface overflow-hidden"
        style={{
          maxWidth: 480,
          borderRadius: 22,
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--color-hairline)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ padding: '22px 26px 4px' }}>
          <div className="font-display font-bold text-ink" style={{ fontSize: 19, letterSpacing: '-0.01em' }}>
            {title}
          </div>
        </div>
        <div style={{ padding: '12px 26px 22px' }}>
          {body}
        </div>
        <div
          className="flex items-center justify-end gap-2"
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--color-hairline)',
            background: 'var(--color-bg-alt)',
          }}
        >
          <button
            onClick={onCancel}
            className="cursor-pointer font-semibold"
            style={{
              padding: '8px 16px',
              borderRadius: 999,
              background: 'transparent',
              color: 'var(--color-ink-soft)',
              border: 0,
              fontSize: 14,
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={confirmDisabled}
            className="cursor-pointer font-semibold"
            style={{
              padding: '9px 18px',
              borderRadius: 999,
              background: confirmDanger ? 'var(--color-peach-deep)' : 'var(--color-ink)',
              color: '#fff',
              border: 0,
              fontSize: 14,
              opacity: confirmDisabled ? 0.5 : 1,
              cursor: confirmDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!getToken()) {
      window.location.href = '/login';
    }
  }, []);

  const refresh = async () => {
    try {
      const me = await api.me();
      setUser(me);
      setLoadError(null);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401 || status === 403) {
        window.location.href = '/login';
        return;
      }
      setLoadError((err as Error).message || 'Could not load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const crumbs = ['Settings'];
  if (user?.email) {
    crumbs.unshift(user.email.split('@')[1]?.split('.')[0] ?? 'Workspace');
  }

  return (
    <div className="flex min-h-screen bg-bg font-body">
      <Sidebar user={user} />

      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar crumbs={crumbs} />

        <div style={{ padding: '28px 32px 80px', maxWidth: 880, width: '100%', margin: '0 auto' }} className="w-full">
          {loading ? (
            <div className="text-center text-ink-soft" style={{ padding: '60px 20px' }}>Loading…</div>
          ) : loadError ? (
            <div className="text-center text-ink-soft" style={{ padding: '60px 20px' }}>{loadError}</div>
          ) : user ? (
            <>
              <div className="mb-6">
                <h1
                  className="font-display font-extrabold text-ink m-0"
                  style={{ fontSize: 32, letterSpacing: '-0.02em' }}
                >
                  Settings
                </h1>
                <p className="text-sm text-ink-soft mt-2 m-0 leading-[1.55]" style={{ maxWidth: 640 }}>
                  Manage your profile, password, and account.
                </p>
              </div>

              <ProfileSection user={user} onSaved={refresh} />
              <PasswordSection user={user} />
              <WorkspaceSection user={user} onSaved={refresh} />
              <ExportSection />
              <DangerSection user={user} />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
